import styles from './ccf-contact-history-table.module.scss';
import { SetStateAction, useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CcfContactHistoryTableHeader from '../ccf-contact-history-table-header/ccf-contact-history-table-header';
import CcfContactHistoryTableContent from '../ccf-contact-history-table-content/ccf-contact-history-table-content';
import { useDispatch, useSelector } from 'react-redux';
import { contactHistoryListData } from '../mock-data/contact-history-mock-data';
import { activeContactSelector } from '../../ccf-inbox/ccf-inbox.slice';
import { getUpdatedContactHistoryList, globalActions } from '../../global.app.slice';
/**
 * Used to find descending comparator in table sorting
 * @example - descendingComparator()
 */
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

/**
 * Used to get comparator for sorting
 * @example - getComparator()
 */
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | any },
  b: { [key in Key]: number | string | any }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/* eslint-disable-next-line */
export interface CcfContactHistoryTableProps {
  isAppSpace: boolean;
}

/**
 * used to show contact history table
 * @param props - CcfContactHistoryTableProps
 * @example ` <CcfContactHistoryTable />`
 */
export function CcfContactHistoryTable(props: CcfContactHistoryTableProps) {
  const { isAppSpace } = props;
  const theme = useTheme();
  const isSmView = useMediaQuery(
    theme.breakpoints.between(
      theme.breakpoints.values.sm,
      theme.breakpoints.values.md
    )
  );
  const [openRow, setOpenRow] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('customer');
  const activeContact = useSelector(activeContactSelector);
  const [contactHistoryCol, setContactHistoryCol] = useState(
    contactHistoryListData
  );
  const {setUpdatedContactHistory} = globalActions;
  const updatedContactHistoryList = useSelector(getUpdatedContactHistoryList);
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeContact?.contactHistoryList) {
    dispatch(setUpdatedContactHistory(activeContact?.contactHistoryList));
    setContactHistoryCol(activeContact?.contactHistoryList);
    }
    else {
      if(updatedContactHistoryList.length > 0)
      setContactHistoryCol(updatedContactHistoryList);
    }
  }, [activeContact]);

  /**
   * Used to handle sort request
   * @param event -React.SyntheticEvent
   * @param property -string
   * @example - handleRequestSort()
   */
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <TableContainer component={Paper} style={{boxShadow:'none'}} >
      <Table 
        aria-label="collapsible table"
        className={styles.table}
        stickyHeader
      >
        <CcfContactHistoryTableHeader
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          isAppSpace={isAppSpace}
        />
        <TableBody className={styles.tableBody}>
          {contactHistoryCol
            ?.slice()
            .sort(getComparator(order, orderBy))
            .map((row) => (
              <CcfContactHistoryTableContent
                key={row.id}
                row={row}
                open={row.customer == openRow}
                onClick={(customer: SetStateAction<string>) =>
                  setOpenRow(customer)
                }
                isAppSpace={isAppSpace}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CcfContactHistoryTable;
